import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { colors } from '../theme';
import EvStationIcon from '@mui/icons-material/EvStation';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function SolutionsSection() {
  const solutions = [
    {
      icon: <EvStationIcon sx={{ fontSize: 50 }} />,
      title: 'EV Charging Solutions',
      description: 'Complete charging infrastructure for commercial and residential applications',
      features: [
        'AC Chargers (7.4kW, 22kW)',
        'Fast DC Chargers (60kW, 120kW, 200kW)',
        'OCPP-compliant CSMS',
        'Smart load management',
        'Remote monitoring & control',
      ],
      color: colors.yellow,
      iconColor: colors.green,
      hoverColor: colors.green,
    },
    {
      icon: <BatteryChargingFullIcon sx={{ fontSize: 50 }} />,
      title: 'Energy Storage System (ESS)',
      description: 'Stabilize and optimize your charging infrastructure',
      features: [
        'Small Remote ESS (NEW)',
        'Stabilizes charging supply',
        'Peak shaving capabilities',
        'Works seamlessly with solar',
        'Grid independence options',
      ],
      color: colors.green,
      highlight: true,
      hoverColor: colors.green,
    },
    {
      icon: <WbSunnyIcon sx={{ fontSize: 50 }} />,
      title: 'Solar Integration',
      description: 'Powered by Solateks - Complete solar solutions',
      features: [
        'Residential, commercial & industrial',
        'Partial or full grid independence',
        'Professional site assessment',
        'Integrated with EV charging',
      ],
      note: 'Solar viability depends on site capacity and roof availability.',
      color: colors.yellow,
      iconColor: colors.yellow,
      hoverColor: colors.yellow,
    },
    {
      icon: <EngineeringIcon sx={{ fontSize: 50 }} />,
      title: 'End-to-End Setup',
      description: 'Turnkey solutions from planning to go-live',
      features: [
        'Site Assessment',
        'System Planning & Design',
        'Professional Installation',
        'Testing & Commissioning',
        'Go-Live & Handover',
      ],
      timeline: 'Typical Timeline: 2-4 Months',
      color: colors.green,
      iconColor: colors.black,
      hoverColor: colors.yellow,
    },
  ];

  return (
    <Box
      id="solutions"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: colors.white,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            Our{' '}
            <Box component="span" sx={{ color: colors.green }}>
              Solutions
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Comprehensive EV charging infrastructure with integrated solar and
            energy storage solutions
          </Typography>
        </Box>

        {/* Solutions Grid */}
        <Grid container spacing={3}>
          {solutions.map((solution, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  border: solution.highlight
                    ? `3px solid ${colors.yellow}`
                    : '1px solid #e0e0e0',
                  position: 'relative',
                  overflow: 'visible',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 16px 48px ${solution.hoverColor}40`,
                  },
                }}
              >
                {solution.highlight && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -15,
                      right: 20,
                      bgcolor: colors.yellow,
                      color: colors.black,
                      px: 3,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      boxShadow: `0 4px 12px ${colors.yellow}60`,
                    }}
                  >
                    NEW
                  </Box>
                )}
                <CardContent sx={{ p: 2.5 }}>
                  {/* Icon */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: `${solution.color}20`,
                      color: solution.iconColor || (solution.color === colors.yellow ? colors.black : solution.color),
                      mb: 2,
                    }}
                  >
                    {React.cloneElement(solution.icon, { sx: { fontSize: 35 } })}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      color: colors.black,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                    }}
                  >
                    {solution.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      mb: 2,
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                    }}
                  >
                    {solution.description}
                  </Typography>

                  {/* Features List */}
                  <List sx={{ py: 0 }}>
                    {solution.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon
                            sx={{
                              color: colors.green,
                              fontSize: 16,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            color: '#444',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* Note */}
                  {solution.note && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#f5f5f5',
                        borderLeft: `4px solid ${colors.green}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontStyle: 'italic',
                          fontSize: '0.8rem',
                        }}
                      >
                        Note: {solution.note}
                      </Typography>
                    </Box>
                  )}

                  {/* Timeline */}
                  {solution.timeline && (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: `${colors.green}10`,
                      }}
                    >
                      <ArrowForwardIcon
                        sx={{ color: colors.green, mr: 1, fontSize: 18 }}
                      />
                      <Typography
                        sx={{
                          color: colors.green,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                        }}
                      >
                        {solution.timeline}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default SolutionsSection;
